/**
 * CB Cart Upsell
 * Optimistic add-to-cart with single-roundtrip section refresh.
 *
 * Perf principles:
 *   - Flip the button to "Ajouté" the instant the user clicks (rollback on failure).
 *   - Use /cart/add.js with `sections` param so the response already contains the
 *     rendered drawer HTML — no second fetch needed for DOM update.
 *   - Fire /cart.js in parallel for cart totals (FreeShippingGoal). Doesn't block UI.
 *   - DO NOT dispatch `cart:refresh` — that triggers a redundant full-page fetch
 *     in the theme's CartDrawer.onCartRefresh listener. Pubsub already updates
 *     the drawer body using the sections HTML.
 */
(function () {
  'use strict';

  var SVG_PLUS = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  var SVG_CHECK = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
  var SVG_X = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function gatherSections() {
    var ids = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: ids } })
    );
    if (!ids.length) {
      document.querySelectorAll('cart-drawer, main-cart').forEach(function (el) {
        var sid = el.getAttribute && el.getAttribute('data-section-id');
        if (sid) ids.push(sid);
      });
    }
    return Array.from(new Set(ids));
  }

  function publishCartUpdate(cartJson) {
    // pubsub triggers CartItems.onCartUpdate which uses cartJson.sections to swap drawer DOM.
    // We deliberately do NOT dispatch cart:refresh — that would trigger a redundant
    // ?section_id= HTTP fetch in CartDrawer.onCartRefresh.
    try {
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.publish && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      }
    } catch (e) {}
    // NOTE: cart:updated is dispatched LATER, after /cart.js fetch returns,
    // so FreeShippingGoal receives a cart object with items_subtotal_price.
    // Dispatching it here with the stub {sections, item_count} caused the FSG
    // to read undefined and render "Dépensez 0 €".
  }

  function dispatchAdded(cartJson, variantId) {
    try {
      document.dispatchEvent(new CustomEvent('cb:upsell:added', { detail: { variantId: variantId, cart: cartJson }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { cart: cartJson }, bubbles: true }));
    } catch (e) {}
  }

  function showToast(widget, msg) {
    if (!widget) return;
    var toast = widget.querySelector('.cb-upsell__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cb-upsell__toast';
      widget.appendChild(toast);
    }
    toast.textContent = msg;
    toast.setAttribute('data-show', 'true');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { toast.setAttribute('data-show', 'false'); }, 2400);
  }

  function flipToAdded(btn) {
    btn.classList.remove('is-loading', 'is-error');
    btn.classList.add('is-added');
    btn.innerHTML = SVG_CHECK + ' Ajouté';
    btn.setAttribute('data-added-at', String(Date.now()));

    // Reshuffle freeze: prevent the upsell widget from being rerendered for ~2s after add
    var widget = btn.closest('.cb-cart-upsell');
    if (widget) {
      widget.setAttribute('data-frozen', '1');
      clearTimeout(widget.__freezeT);
      widget.__freezeT = setTimeout(function () {
        widget.removeAttribute('data-frozen');
      }, 2000);
    }
  }

  function flipToError(btn, origLabel, widget, msg) {
    btn.classList.remove('is-loading', 'is-added');
    btn.classList.add('is-error');
    btn.innerHTML = SVG_X + ' Réessayer';
    showToast(widget, msg || 'Impossible d’ajouter. Réessayez.');
    setTimeout(function () {
      btn.classList.remove('is-error');
      btn.innerHTML = origLabel;
    }, 2400);
  }

  /**
   * Single add-to-cart entry point. Used by both direct "Ajouter" buttons
   * and by variant-chip clicks.
   *
   * @param {HTMLElement} btn - the originating button (state owner)
   * @param {string} variantId
   * @param {HTMLElement} widget - the .cb-cart-upsell container
   */
  function addItem(btn, variantId, widget) {
    if (!variantId) return;
    if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;

    var origLabel = btn.innerHTML;

    // 1) Optimistic UI — flip immediately, before network
    flipToAdded(btn);

    // 2) POST /cart/add.js with sections — single roundtrip for DOM update
    var sectionIds = gatherSections();
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sectionIds,
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (addResult) {
        if (!addResult.ok) {
          var msg = (addResult.data && (addResult.data.description || addResult.data.message)) || 'Erreur';
          flipToError(btn, origLabel, widget, msg);
          return;
        }

        // Publish IMMEDIATELY using only the sections from the add response.
        // CartItems.onCartUpdate will swap the drawer DOM with the freshly-rendered
        // HTML. Inside that HTML, free-shipping-goal carries the new server-rendered
        // cart total in its data attribute, and re-mounts itself with the correct
        // value — no need to wait for /cart.js for visual update.
        var sections = addResult.data && addResult.data.sections;
        if (sections) {
          publishCartUpdate({ sections: sections, item_count: 1 /* >0 so drawer body stays visible */ });
        }

        // Async: pull the canonical cart, then fire cart:updated with full cart
        // (FreeShippingGoal needs items_subtotal_price) and dispatch analytics.
        fetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (sections) cart.sections = sections;
            try {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart }, bubbles: true }));
            } catch (e) {}
            dispatchAdded(cart, variantId);
          })
          .catch(function () { dispatchAdded({}, variantId); });
      })
      .catch(function () {
        flipToError(btn, origLabel, widget);
      });
  }

  // ---------- Variant picker ----------

  function openVariantPicker(btn) {
    var card = btn.closest('.cb-upsell__card');
    if (!card || card.querySelector('.cb-upsell__variants')) return;

    var raw = btn.getAttribute('data-variants') || '[]';
    var variants;
    try { variants = JSON.parse(raw); } catch (e) { variants = []; }
    if (!variants.length) return;

    var overlay = document.createElement('div');
    overlay.className = 'cb-upsell__variants';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Choisir une option');

    var title = document.createElement('p');
    title.className = 'cb-upsell__variants-title';
    title.textContent = 'Choisir une option';
    overlay.appendChild(title);

    var list = document.createElement('div');
    list.className = 'cb-upsell__variants-list';
    variants.forEach(function (v) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cb-upsell__variant-chip';
      chip.setAttribute('data-variant-id', String(v.id));
      if (!v.available) {
        chip.disabled = true;
        chip.classList.add('is-unavailable');
      }
      chip.innerHTML = '<span class="cb-upsell__variant-title">' + String(v.title).replace(/</g, '&lt;') + '</span>';
      list.appendChild(chip);
    });
    overlay.appendChild(list);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'cb-upsell__variants-close';
    closeBtn.setAttribute('aria-label', 'Fermer');
    closeBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    overlay.appendChild(closeBtn);

    card.appendChild(overlay);
    overlay.addEventListener('click', function (e) { e.stopPropagation(); });

    var first = overlay.querySelector('.cb-upsell__variant-chip:not([disabled])');
    if (first) first.focus();
  }

  function closeVariantPicker(card) {
    if (!card) return;
    var overlay = card.querySelector('.cb-upsell__variants');
    if (overlay) overlay.remove();
  }

  // ---------- Click router ----------

  function onClick(e) {
    // Close X
    var closeBtn = e.target.closest('.cb-upsell__variants-close');
    if (closeBtn) {
      e.preventDefault(); e.stopPropagation();
      closeVariantPicker(closeBtn.closest('.cb-upsell__card'));
      return;
    }
    // Variant chip → add
    var chip = e.target.closest('.cb-upsell__variant-chip');
    if (chip && !chip.disabled) {
      e.preventDefault(); e.stopPropagation();
      var card = chip.closest('.cb-upsell__card');
      var widget = chip.closest('.cb-cart-upsell');
      var chooseBtn = card && card.querySelector('.cb-upsell__btn--choose');
      var overlay = card && card.querySelector('.cb-upsell__variants');
      if (overlay) overlay.remove();
      if (chooseBtn) addItem(chooseBtn, chip.getAttribute('data-variant-id'), widget);
      return;
    }
    // Main button
    var btn = e.target.closest('.cb-upsell__btn');
    if (!btn) return;
    if (btn.getAttribute('data-multi-variant') === 'true') {
      e.preventDefault(); e.stopPropagation();
      openVariantPicker(btn);
      return;
    }
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return; // links handle themselves
    e.preventDefault(); e.stopPropagation();
    addItem(btn, variantId, btn.closest('.cb-cart-upsell'));
  }

  // ---------- Recently-viewed tracking ----------
  // Listen for product page views; store last 8 product IDs in localStorage.
  function recordRecentlyViewed() {
    try {
      // Detect product page via meta or path
      var pid = null;
      var meta = document.querySelector('meta[property="og:product:retailer_item_id"], meta[name="shopify-checkout-api-token"]');
      var product = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (product && product.id) pid = String(product.id);
      if (!pid) {
        var match = location.pathname.match(/\/products\/([^/?#]+)/);
        if (match) pid = 'h:' + match[1]; // handle as fallback
      }
      if (!pid) return;
      var raw = localStorage.getItem('cb_recently_viewed') || '[]';
      var arr;
      try { arr = JSON.parse(raw); } catch (e) { arr = []; }
      arr = arr.filter(function (x) { return x !== pid });
      arr.unshift(pid);
      arr = arr.slice(0, 8);
      localStorage.setItem('cb_recently_viewed', JSON.stringify(arr));
    } catch (e) {}
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordRecentlyViewed);
  } else {
    recordRecentlyViewed();
  }

  // ---------- Keyboard arrow navigation between cards ----------
  function onCardKeydown(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var card = e.target.closest('.cb-upsell__card');
    if (!card) return;
    e.preventDefault();
    var cards = card.parentElement.querySelectorAll('.cb-upsell__card');
    var idx = Array.prototype.indexOf.call(cards, card);
    var nextIdx = e.key === 'ArrowRight' ? Math.min(idx + 1, cards.length - 1) : Math.max(idx - 1, 0);
    var nextCard = cards[nextIdx];
    if (!nextCard) return;
    var btn = nextCard.querySelector('.cb-upsell__btn');
    if (btn) btn.focus();
    nextCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  // ---------- Reset stuck "Ajouté" if user removes that variant from cart ----------

  function onCartUpdated(e) {
    var cart = e && e.detail && e.detail.cart;
    if (!cart || !cart.items) return;
    var inCart = {};
    cart.items.forEach(function (it) { inCart[String(it.variant_id)] = true; });
    document.querySelectorAll('.cb-upsell__btn.is-added').forEach(function (btn) {
      var vid = btn.getAttribute('data-variant-id');
      if (!inCart[String(vid)]) {
        btn.classList.remove('is-added');
        var label = btn.getAttribute('data-label') || 'Ajouter';
        btn.innerHTML = SVG_PLUS + ' ' + label;
      }
    });
  }

  // ---------- View tracking ----------

  function dispatchView(widget) {
    try {
      document.dispatchEvent(new CustomEvent('cb:upsell:view', {
        detail: { sectionId: widget.getAttribute('data-section-id') },
        bubbles: true
      }));
    } catch (e) {}
  }

  // ---------- Related-products enhancement ----------
  // After mount, swap the static (Liquid-rendered, collection-based) cards for
  // products related to the first item in the cart, fetched from
  // /recommendations/products.json. If the API returns < 3 usable products,
  // we keep the Liquid fallback.

  function moneyFmt(cents) {
    try {
      var fmt = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return fmt.format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function buildCardHtml(p, gapCents, freeShipHit) {
    var v = (p.variants && p.variants[0]) || {};
    var price = v.price != null ? v.price : p.price;
    var compareAt = v.compare_at_price != null ? v.compare_at_price : p.compare_at_price;
    var onSale = compareAt && compareAt > price;
    var imgRaw = (p.featured_image && p.featured_image.src) || p.featured_image || '';
    // featured_image can be relative (//cdn...) — normalize
    var img = '';
    if (imgRaw) {
      img = imgRaw.indexOf('//') === 0 ? 'https:' + imgRaw : imgRaw;
    }
    // Append size hint
    var imgSized = img;
    if (img && img.indexOf('?') === -1) {
      imgSized = img.replace(/(\.[a-z0-9]+)(?=$|\?)/i, '_400x$1') || img;
    }

    var priceHtml = '<span class="cb-upsell__price' + (onSale ? ' cb-upsell__price--sale' : '') + '">' +
      escapeHtml(moneyFmt(price)) +
      (onSale ? '<span class="cb-upsell__price-compare">' + escapeHtml(moneyFmt(compareAt)) + '</span>' : '') +
      '</span>';

    return '' +
      '<div class="cb-upsell__card" role="listitem" data-product-id="' + p.id + '">' +
        '<a class="cb-upsell__card-link" href="' + escapeHtml(p.url) + '" aria-label="' + escapeHtml(p.title) + '"></a>' +
        '<div class="cb-upsell__img-wrap">' +
          (img ? '<img src="' + escapeHtml(imgSized) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="140" height="140">' : '') +
        '</div>' +
        '<div class="cb-upsell__body">' +
          '<div class="cb-upsell__stars" data-has-reviews="false" aria-hidden="true"></div>' +
          '<p class="cb-upsell__name">' + escapeHtml(p.title) + '</p>' +
          '<div class="cb-upsell__price-row">' +
            priceHtml +
            '<button class="cb-upsell__btn" type="button" data-variant-id="' + v.id + '" data-label="Ajouter" aria-label="Ajouter ' + escapeHtml(p.title) + ' au panier">' +
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
              ' Ajouter' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  // sessionStorage cache for /recommendations/products.json results
  function getCachedRelated(productId) {
    try {
      var raw = sessionStorage.getItem('cb_related_' + productId);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.t || Date.now() - entry.t > 10 * 60 * 1000) return null; // 10min TTL
      return entry.products;
    } catch (e) { return null; }
  }
  function setCachedRelated(productId, products) {
    try {
      sessionStorage.setItem('cb_related_' + productId, JSON.stringify({ t: Date.now(), products: products }));
    } catch (e) {}
  }

  function getRecentlyViewedSet() {
    try {
      var raw = localStorage.getItem('cb_recently_viewed') || '[]';
      var arr = JSON.parse(raw);
      var set = {};
      arr.forEach(function (x) { set[String(x)] = true; });
      return set;
    } catch (e) { return {}; }
  }

  function pickRelated(products, inCartSet, gapCents, freeShipHit) {
    // Boost recently-viewed products to the front, preserving relative order otherwise
    var recent = getRecentlyViewedSet();
    var sorted = products.slice().sort(function (a, b) {
      var ar = recent[String(a.id)] ? 1 : 0;
      var br = recent[String(b.id)] ? 1 : 0;
      return br - ar;
    });

    var picked = [];
    var typesSeen = {};
    for (var i = 0; i < sorted.length && picked.length < 3; i++) {
      var p = sorted[i];
      if (!p.available) continue;
      if ((p.variants_count || (p.variants && p.variants.length) || 1) > 1) continue;
      var v = (p.variants && p.variants[0]) || {};
      if (!v.id || inCartSet[String(v.id)]) continue;
      // Below free-shipping threshold: never overshoot the gap
      if (!freeShipHit && gapCents > 0) {
        var price = v.price != null ? v.price : p.price;
        if (price > gapCents) continue;
      }
      var t = p.type || '';
      if (t && typesSeen[t]) continue;
      if (t) typesSeen[t] = true;
      picked.push(p);
    }
    return picked;
  }

  function loadRelated(widget) {
    // Respect reshuffle freeze
    if (widget.getAttribute('data-frozen') === '1') return;

    var firstProductId = widget.getAttribute('data-cart-first-product-id');
    if (!firstProductId || firstProductId === '0') return;
    if (widget.__relatedFor === firstProductId) return;
    widget.__relatedFor = firstProductId;

    var inCart = (widget.getAttribute('data-cart-variant-ids') || '').split(',').filter(Boolean);
    var inCartSet = {};
    inCart.forEach(function (id) { inCartSet[String(id)] = true; });
    var freeShipHit = widget.getAttribute('data-free-shipping-hit') === 'true';
    var gapCents = parseInt(widget.getAttribute('data-gap-cents') || '0', 10);

    function applyProducts(products) {
      var picked = pickRelated(products, inCartSet, gapCents, freeShipHit);
      if (picked.length < 3) return false;
      var track = widget.querySelector('.cb-upsell__track');
      if (!track) return false;
      track.innerHTML = picked.map(function (p) { return buildCardHtml(p, gapCents, freeShipHit); }).join('');
      return true;
    }

    // 1) Try cache (instant, no network)
    var cached = getCachedRelated(firstProductId);
    if (cached && applyProducts(cached)) return;

    // 2) Fetch fresh
    fetch('/recommendations/products.json?product_id=' + firstProductId + '&intent=related&limit=20', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) return;
        setCachedRelated(firstProductId, data.products);
        applyProducts(data.products);
      })
      .catch(function () {});
  }

  function initObserver() {
    document.querySelectorAll('.cb-cart-upsell').forEach(function (w) {
      loadRelated(w);
      if (w.__cbSeen) return;
      w.__cbSeen = true;
      dispatchView(w);
    });
  }

  // ---------- Bind (idempotent for hot-reload + drawer rerender) ----------

  if (window.__cbUpsellClick) document.removeEventListener('click', window.__cbUpsellClick);
  window.__cbUpsellClick = onClick;
  document.addEventListener('click', onClick);

  if (window.__cbUpsellCartUpdated) document.removeEventListener('cart:updated', window.__cbUpsellCartUpdated);
  window.__cbUpsellCartUpdated = onCartUpdated;
  document.addEventListener('cart:updated', onCartUpdated);

  if (window.__cbUpsellKeydown) document.removeEventListener('keydown', window.__cbUpsellKeydown);
  window.__cbUpsellKeydown = onCardKeydown;
  document.addEventListener('keydown', onCardKeydown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver);
  } else {
    initObserver();
  }
  document.addEventListener('cart:updated', function () { setTimeout(initObserver, 50); });
})();
